import { ConnectionCard } from 'components/ConnectionCard';
import {
  HStack,
  Skeleton,
  Stack,
  StackDivider,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { truncateUuid } from 'utils/truncateUuid';
import { ClipboardButton } from 'components/ClipboardButton';
import { InfoIcon } from 'shared/InfoIcon';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlag } from '../../constant/feature-flags';

const DetailItem = ({
  label,
  value,
  isLoading,
  textToCopy,
  tooltipText,
}: {
  label: string;
  value?: string | null;
  isLoading?: boolean;
  tooltipText?: string;
  textToCopy?: string | null;
}) => {
  return (
    <Stack
      flexBasis="25%"
      alignSelf="flex-start"
      p={{ base: '2', lg: '6' }}
      px={{ base: '4', lg: '6' }}
    >
      <HStack>
        <Text fontSize={{ base: 'xs', lg: 'sm' }} fontWeight={500}>
          {label}
        </Text>
        {tooltipText && (
          <Tooltip label={tooltipText} w="280px">
            <div>
              <InfoIcon />
            </div>
          </Tooltip>
        )}
      </HStack>
      <HStack justify="space-between">
        <Text fontSize={{ base: 'md', lg: '2xl' }} fontWeight={600}>
          {isLoading ? (
            <Skeleton
              height="40px"
              width={{ base: '256px', lg: '100px', xl: '256px' }}
              rounded="lg"
            />
          ) : (
            (value ?? '—')
          )}
        </Text>
        {textToCopy && <ClipboardButton iconSize="20px" value={textToCopy} />}
      </HStack>
    </Stack>
  );
};

// TEMP: code just to hide real company names for pensionpro webinar demo
const demoNames: Record<string, string> = {
  '106a374a-b9a6-45f5-81e4-55daafddb1f2': "Fabio's Falafel",
  '9f45f1fc-decc-446a-b924-f34a282d73e6': 'Chris Cafe',
  'd7c061bc-a897-47e7-b1c5-2ad0473564aa': "Dom's Dentist",
  'f0e44550-f0cc-4d99-baff-7a05ce34cc2e': 'Speed World',
  'e7acc518-4254-4fc0-a94e-0401190bdfa6': "Stefano's Italian Restaurant",
};

export const ConnectionDetailHeader = ({
  companyName,
  companyId,
  customerName,
  customerId,
  isLoading,
  isStaged,
}: {
  companyName?: string | null;
  companyId?: string | null;
  customerName?: string | null;
  customerId?: string | null;
  isLoading?: boolean;
  isStaged?: boolean;
}) => {
  const isWebinar = useFlag(FeatureFlag.PensionProWebinar);

  return (
    <ConnectionCard>
      <Stack
        divider={<StackDivider borderColor="gray.200" />}
        align="flex-start"
        direction={{ base: 'column', lg: 'row' }}
        spacing="0"
      >
        <DetailItem
          label="Company Name"
          value={isWebinar && companyId ? demoNames[companyId] : companyName}
          isLoading={isLoading}
          tooltipText={
            isStaged
              ? 'A Company Name will be pulled from the provider when an access token is created'
              : undefined
          }
        />
        <DetailItem
          label="Company ID"
          value={companyId ? truncateUuid(companyId) : undefined}
          isLoading={isLoading}
          textToCopy={companyId}
          tooltipText={
            isStaged
              ? 'A Company ID will be generated when an access token is created'
              : undefined
          }
        />
        <DetailItem
          label="Customer Name"
          value={customerName}
          isLoading={isLoading}
        />
        <DetailItem
          label="Customer ID"
          value={customerId}
          isLoading={isLoading}
        />
      </Stack>
    </ConnectionCard>
  );
};
